import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'
import { populateCompiledDataTable } from '../data-hub/table'
import { clearSummariesCache } from '@/services/localCacheService'
import { showAlertModal } from '@/components/alert-modal'
import { uploadStore } from './store'

/**
 * Listens for real-time status updates on a Firestore document.
 * Updates the Global Upload Store and refreshes ONLY when the batch is done.
 */
export function listenForProcessingStatus(jobId: string, storeId: string) {
  if (!currentUser) return

  // Update store to processing state
  uploadStore.updateItem(storeId, { status: 'processing', message: 'Queued on server...', progress: 0 })

  const jobDocRef = doc(db, `processingJobs`, jobId)

  const unsubscribe = onSnapshot(jobDocRef, async (docSnap) => {
    if (!docSnap.exists()) {
      uploadStore.updateItem(storeId, { status: 'error', message: 'Job document lost', progress: 0 })
      unsubscribe()
      return
    }

    const jobData = docSnap.data()
    const status = jobData?.status
    const progress = jobData?.progress

    if (status === 'processing' && progress) {
      const percent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0
      uploadStore.updateItem(storeId, {
        status: 'processing',
        progress: percent,
        message: progress.message || 'Processing data...'
      })

    } else if (status === 'complete') {
      
      // 1. Mark THIS specific upload as complete
      uploadStore.updateItem(storeId, {
        status: 'complete',
        progress: 100,
        message: 'Done!'
      })
      
      // 2. Clear cache silently
      try {
        await clearSummariesCache()
      } catch (e) {
        console.warn('Cache clear failed', e)
      }

      // 3. Attempt to update the table (This causes your current error, but we keep it for now)
      try {
        await populateCompiledDataTable()
      } catch (err) {
        console.error('Table update failed:', err)
      }
      
      // 4. CHECK IF ALL UPLOADS ARE DONE
      // We check if any OTHER items in the store are still working
      const allItems = uploadStore.getItems()
      const stillWorking = allItems.some(item => 
        ['preparing', 'uploading', 'processing'].includes(item.status) && 
        item.id !== storeId // ignore self
      )

      // Only refresh if NO other items are working
      if (!stillWorking) {
        console.log('All uploads complete. Refreshing...')
        setTimeout(() => {
          location.reload()
        }, 1500) // Small delay so user sees "100% Done" green bars briefly
      }
      
      unsubscribe()

    } else if (status === 'error') {
      const errorMessage = jobData?.['progress.message'] || progress?.message || 'Processing failed'
      
      uploadStore.updateItem(storeId, {
        status: 'error',
        message: errorMessage,
        progress: 0
      })

      if (typeof errorMessage === 'string' && errorMessage.startsWith('[BRANCH_NAME_MISMATCH]')) {
        showAlertModal({
          title: 'Branch Name Mismatch',
          message: errorMessage.replace('[BRANCH_NAME_MISMATCH] ', ''),
          confirmLabel: 'Understood',
        })
      }

      unsubscribe()
    }
  })
}